<template>
  <div class="settings-wrp" :key="route.path">
    <div class="settings-title">
      {{ t('Upload Conversions') }}
    </div>
    <div class="settings-content">
      <div class="settings-block">
        <div
          class="start-text"
          v-html="
            t(
              `<p>If some conversions are not appearing in BeMob due to the incorrect settings you can upload them manually.</p><p>The values should be organized in the following order: click ID;payout;transaction ID;status or custom conversion value.<br>e.g. BuVsxm8W4aCX6NdKeKSPz3;3.5;ee56789ru;sell - for conversions with the <b>status</b> value.</p><p>To upload manually a <b>custom conversion</b> it is required to add ;; before the conversion event value, e.g.<br>BuVsxm8W4aCX6NdKeKSPz3;5.12;TRANSACTIONID;;accept</p><p>If a parameter is not supported and as a result not passed via postback , it is required to specify ; instead.<br>e.g. BuVsxm8W4aCX6NdKeKSPz3;3.5;;;sell</p><p>Each conversion record should start with a new line.</p><p>More details on manual conversion uploading learn {0}.</p>`,
              [
                `<a href='https://docs.bemob.com/${locale}/uploading-conversions-manually' target='_blank' rel='noopener noreferrer'>${t('here')}</a>`,
              ],
            )
          "
        />
        <div class="second-text">
          <div>
            <div class="subtitle">{{ t('From Affiliate Network') }}</div>
            <div
              class="text"
              v-html="
                t(
                  'Add the large amount of conversions at once straight from the file.<br>Click on Upload Conversions to start.',
                )
              "
            />
          </div>
          <ui-button
            class="mr-0 mt-4"
            :text="t('Upload Conversions')"
            icon="fas fa-cloud-upload-alt"
            button-style="accent"
            :loading="fileUploading"
            @click="uploadFile"
          />
          <input
            ref="fileInputRef"
            class="upload-conversions__file-input"
            type="file"
            :multiple="false"
            accept=".txt"
            @change="onFileChange"
          />
        </div>

        <div class="mb-16 conversion-block">
          <div class="subtitle">{{ t('For Uploading Conversions Manually') }}</div>
          <div
            class="text"
            v-html="
              t(
                'Introduce the available parameter of conversion.<br>When finished click on Upload.',
              )
            "
          />
        </div>
        <ui-textarea
          v-model="conversion"
          placeholder="BuVsxm8W4aCX6NdKeKSPz3;5.12;TRANSACTIONID;accept"
          rows="20"
        />
        <div class="text-right">
          <ui-button
            class="mr-0 justify-end pt-8"
            :text="t('Upload')"
            icon="fas fa-cloud-upload-alt"
            button-style="accent"
            :loading="conversionsUploading"
            @click="uploadConversions"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from '@/composables';
import { $emitter } from '@/services/emitter';
import { postUserUploadConversionsApiRequest } from '@/services/api/user-api';

const route = useRoute();
const { t, locale } = useI18n();

const fileInputRef = ref<HTMLInputElement | null>(null);
const conversion = ref('');
const fileUploading = ref(false);
const conversionsUploading = ref(false);

const uploadFile = () => {
  fileInputRef.value?.click();
};

const onFileChange = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const fileList = input.files;
  if (!fileList?.length) {
    return;
  }
  const file = fileList[0];
  if (file.size > 2097152) {
    $emitter.emit('toast-error', t('File is too big. Maximum file size is 2Mb'));
    return;
  }

  const reader = new FileReader();
  reader.onload = () => {
    const text = typeof reader.result === 'string' ? reader.result : '';
    fileUploading.value = true;
    postUserUploadConversionsApiRequest({ conversions: text.replace(/\r/gi, '') })
      .then(() => {
        $emitter.emit('toast-success', t('Conversions uploaded successfully.'));
      })
      .catch(() => {
        $emitter.emit('toast-error', t('Could not upload conversions.'));
      })
      .finally(() => {
        fileUploading.value = false;
      });
  };
  reader.readAsText(file);
  input.value = '';
};

const uploadConversions = async () => {
  conversionsUploading.value = true;
  try {
    await postUserUploadConversionsApiRequest({ conversions: conversion.value });
    $emitter.emit('toast-success', t('Conversions uploaded successfully.'));
    conversion.value = '';
  } catch {
    $emitter.emit('toast-error', t('Could not upload conversions.'));
  } finally {
    conversionsUploading.value = false;
  }
};
</script>

<style lang="scss" scoped>
.upload-conversions__file-input {
  display: none;
}

.start-text {
  margin-bottom: 12px;
  border-bottom: 1px solid var(--color-gray10) !important;
}

.second-text {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 0 16px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--color-gray10) !important;
}

.subtitle {
  font-size: 18px;
  font-weight: bold;
}
</style>
